import { SubmitHandler } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaign } from 'api/campaigns';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { CAMPAIGN_FEE_TYPE_LINK } from 'constants/links';
import { Settings } from 'constants/settings';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { Campaign } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface FeesFormValues {
    disableTips: boolean;
    hideFees: boolean;
    requireFees: boolean;
}

const CampaignFeeSettings = () => {
    const toast = useToast();
    const { data: campaign } = useCurrentCampaign();
    const methods = Form.useGivebutterForm<FeesFormValues>({
        defaultValues: {
            disableTips: campaign?.disableTips?.active ?? false,
            hideFees: campaign?.hideFees?.active ?? false,
            requireFees: campaign?.requireFees?.active ?? false,
        },
    });
    const { getValues, setValue, control, path, handleSubmit } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: FeesFormValues) => {
            const data = {
                settings: [
                    {
                        code: Settings.DisableTips.Code,
                        active: values.disableTips,
                    },
                    {
                        code: Settings.HideFees.Code,
                        active: values.hideFees,
                    },
                    {
                        code: Settings.RequireFees.Code,
                        active: values.requireFees,
                    },
                ],
            };

            return updateCampaign({ campaignId: campaign?.id!, data });
        },
        onSuccess: () => toast.success('Updated fees & tips settings successfully!'),
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update fees & tips settings'),
    });

    return (
        <Form.Provider {...methods}>
            <form onSubmit={handleSubmit(submit as SubmitHandler<FeesFormValues>)}>
                <SettingsCard.Root>
                    <SettingsCard.LeftSide>
                        <SettingsCard.Heading>Fees & tips</SettingsCard.Heading>
                        <p className={'text-sm text-gray-700'}>
                            Configure the fees on your campaign.
                        </p>
                    </SettingsCard.LeftSide>
                    <SettingsCard.RightSide>
                        <div className={'flex flex-col gap-4'}>
                            {campaign?.can_edit_tip && (
                                <>
                                    <div className={'flex flex-col gap-2'}>
                                        <div className={'flex items-center justify-between gap-2'}>
                                            <Form.Label
                                                className={'text-base'}
                                                name={path('disableTips')}
                                            >
                                                Disable tips
                                            </Form.Label>
                                            <Form.Switch control={control} name={'disableTips'} />
                                        </div>
                                        <p className={'text-sm text-gray-700'}>
                                            Ask supporters to leave an optional tip for Givebutter.
                                            Turning this on will increase our platform fee from 0%
                                            to {getUpperFeeByCampaignType(campaign?.type!)}%.{' '}
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={CAMPAIGN_FEE_TYPE_LINK}
                                            >
                                                Learn more
                                            </a>
                                            .
                                        </p>
                                    </div>
                                    <Separator />
                                </>
                            )}
                            <div className={'flex flex-col gap-2'}>
                                <div className={'flex items-center justify-between gap-2'}>
                                    <Form.Label className={'text-base'} name={path('hideFees')}>
                                        Hide fees
                                    </Form.Label>
                                    <Form.Switch
                                        control={control}
                                        name={'hideFees'}
                                        onCheckedChange={checked => {
                                            if (checked && getValues('requireFees')) {
                                                setValue('requireFees', false);
                                            }
                                        }}
                                    />
                                </div>
                                <p className={'text-sm text-gray-700'}>
                                    Turn this on if you'd like to hide transaction fees from
                                    supporters.
                                </p>
                            </div>
                            <Separator />
                            <div className={'flex flex-col gap-2'}>
                                <div className={'flex items-center justify-between gap-2'}>
                                    <Form.Label className={'text-base'} name={path('requireFees')}>
                                        Require fees
                                    </Form.Label>
                                    <Form.Switch
                                        control={control}
                                        name={'requireFees'}
                                        onCheckedChange={checked => {
                                            if (checked && getValues('hideFees')) {
                                                setValue('hideFees', false);
                                            }
                                        }}
                                    />
                                </div>
                                <p className={'text-sm text-gray-700'}>
                                    Turn this on if you'd like to require supporters to cover the
                                    transaction fees.
                                </p>
                            </div>
                            <div>
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </div>
                        </div>
                    </SettingsCard.RightSide>
                </SettingsCard.Root>
            </form>
        </Form.Provider>
    );
};

const getUpperFeeByCampaignType = (type: Campaign['type']) => {
    switch (type) {
        case 'general':
        case 'collect':
            return '1';
        case 'fundraise':
            return '3';
        case 'event':
        case 'external':
        default:
            return '5';
    }
};

export { CampaignFeeSettings };
